import React, { useMemo } from 'react';
import { Dropdown, Form, Button, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { TaskFiltersProps } from '../types/types';
import {
  priorityOptions,
  sortByOptions,
  sortOrderOptions,
  statusOptions
} from '../config/data';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const parseDateString = (dateString?: string) => {
  if (!dateString) return null;

  const parts = dateString.split('-');
  if (parts.length !== 3) return null;

  const [year, month, day] = parts;
  const yearNum = Number(year);
  const monthNum = Number(month);
  const dayNum = Number(day);

  if (!yearNum || !monthNum || !dayNum) return null;

  return new Date(yearNum, monthNum - 1, dayNum);
};

const TaskFilters: React.FC<TaskFiltersProps> = ({
  filters,
  onFilterChange,
  onApplyFilters,
  onResetFilters,
  users = []
}) => {
  const dueDateValue = useMemo(
    () => parseDateString(filters.dueDate || ''),
    [filters.dueDate]
  );

  return (
    <Dropdown className="me-2" align="end">
      <Dropdown.Toggle variant="outline-primary" id="dropdown-filters">
        <FontAwesomeIcon icon={faFilter} className="me-2" />
        Filters
      </Dropdown.Toggle>
      <Dropdown.Menu className="p-3" style={{ width: '300px' }}>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Creator</Form.Label>
            <Form.Select
              value={filters.creator}
              onChange={e => onFilterChange('creator', e.target.value)}
              aria-label="Filter by creator"
            >
              <option value="">Select Creator</option>
              {users.map(user => (
                <option
                  key={`creator-${(user as any).id || user._id}`}
                  value={(user as any).id || user._id}
                >
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>Filter by task creator</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Assigned To</Form.Label>
            <Form.Select
              value={filters.assignedTo}
              onChange={e => onFilterChange('assignedTo', e.target.value)}
              aria-label="Filter by assigned user"
            >
              <option value="">Select Assigned User</option>
              {users.map(user => (
                <option
                  key={`assignedTo-${(user as any).id || user._id}`}
                  value={(user as any).id || user._id}
                >
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>Filter by assigned user</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Priority</Form.Label>
            <Form.Select
              value={filters.priority}
              onChange={e => onFilterChange('priority', e.target.value)}
              aria-label="Filter by priority"
            >
              <option value="">Select Priority</option>
              {priorityOptions.map(option => (
                <option key={`priority-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>Filter by priority (low, medium, high)</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={filters.status}
              onChange={e => onFilterChange('status', e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Select Status</option>
              {statusOptions.map(option => (
                <option key={`status-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>
              Filter by status (not started, in progress, completed)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3 d-flex flex-column">
            <Form.Label className="mb-1">Due Date</Form.Label>
            <DatePicker
              selected={dueDateValue}
              onChange={dateValue => {
                const selectedDate = Array.isArray(dateValue)
                  ? dateValue[0]
                  : dateValue;
                onFilterChange(
                  'dueDate',
                  selectedDate ? selectedDate.toISOString().split('T')[0] : ''
                );
              }}
              placeholderText="mm/dd/yyyy"
              className="form-control"
              wrapperClassName="w-100"
              dateFormat="MM/dd/yyyy"
              isClearable
            />
            <Form.Text muted>Filter by due date (MM/DD/YYYY)</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sort By</Form.Label>
            <Form.Select
              value={filters.sortBy}
              onChange={e => onFilterChange('sortBy', e.target.value)}
              aria-label="Sort by field"
            >
              <option value="">Select Sort By</option>
              {sortByOptions.map(option => (
                <option key={`sortBy-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>
              Sort by field (dueDate, priority, createdAt)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sort Order</Form.Label>
            <Form.Select
              value={filters.sortOrder}
              onChange={e => onFilterChange('sortOrder', e.target.value)}
              aria-label="Sort order"
            >
              <option value="">Select Sort Order</option>
              {sortOrderOptions.map(option => (
                <option key={`sortOrder-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>Sort order (asc, desc)</Form.Text>
          </Form.Group>

          <ButtonGroup className="w-100">
            <Button
              variant="primary"
              className="flex-grow-1"
              onClick={onApplyFilters}
            >
              Apply Filters
            </Button>
            <Button
              variant="outline-secondary"
              className="flex-grow-1"
              onClick={onResetFilters}
            >
              Reset
            </Button>
          </ButtonGroup>
        </Form>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default TaskFilters;
